import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseService } from './base.service';
import { BlueprintStore } from './stores/blueprint.store';
import { ProjectStore } from './stores/projects.store';
import { WorkspaceStore } from './stores/workspace.store';
import { ToolbarEventManagerHelper } from './event-managers/toolbar-event-manager.helper';
import { IdHelper } from './helpers/id.helper';
import { richTextToPlainText } from './helpers/rich-text-content.helper';
import { INodeInstanceModel } from '../models/node/node-instance.model';
import { IntelliCatchMessageAction } from '../models/copilot/intellicatch-message.enum';
import {
  CHAT_IFRAME_SELECTOR,
  IIntelliCatchClearItemContextPayload,
  IIntelliCatchCreateTarget,
  IIntelliCatchHostMessage,
  IIntelliCatchItemContext,
  IIntelliCatchItemSession,
  IIntelliCatchResetConversationPayload,
  IIntelliCatchSetItemContextPayload,
  INTELLICATCH_ITEM_CONTEXT_DESCRIPTION_MAX_CHARS,
} from '../models/copilot/intellicatch-item-context.model';
import * as UiMessageConstants from '../modules/catch-core/data/constants/ui-message.constants';

/**
 * How a context message reaches a widget that may not be there yet. The user can press the button
 * before the iframe has been created — the window is only shown on that same click — so a single
 * post to whatever `document.querySelector` returns silently delivers nothing.
 *
 * Two independent gaps, closed separately: the element itself may be missing, which the retries
 * below cover; and the element may be present while the widget's own message listener is not yet
 * installed, which the `load` handler covers by sending the held context again. A repeat carries
 * the same session id, which the contract defines as a resume rather than a new conversation.
 */
const DELIVERY_RETRY_MS = 250;
const DELIVERY_ATTEMPTS = 8;

/**
 * Owns the work items attached to an IntelliCatch conversation: which one new answers link to, every
 * one attached during the conversation, and every route by which the attachment changes.
 *
 * Closing the window only hides it, and starting on another item does not clear the conversation.
 * The conversation, the attached item and any answer still being generated carry on, and each answer
 * keeps the item it was generated for, so its suggestions create under that item whatever has
 * happened since. Only switching project starts the conversation over.
 */
@Injectable({
  providedIn: 'root',
})
export class IntelliCatchItemContextService extends BaseService {
  private _itemContext = new BehaviorSubject<IIntelliCatchItemContext | null>(null);
  public $itemContext: Observable<IIntelliCatchItemContext | null> = this._itemContext.asObservable();

  private _resultsReady = new BehaviorSubject<boolean>(false);
  /** True when an answer finished while the window was closed, until the window is opened again. */
  public $resultsReady: Observable<boolean> = this._resultsReady.asObservable();

  /** Every item attached during the widget's current conversation, by session id. */
  private sessions = new Map<string, IIntelliCatchItemSession>();
  private generatingSessionId: string | null = null;
  /** The project the conversation belongs to; switching away from it starts the conversation over. */
  private conversationProjectId = '';
  private isOpeningForItem = false;
  private pendingMessage: IIntelliCatchHostMessage | null = null;
  private deliveryTimer: any = null;
  private watchedIframe: HTMLIFrameElement | null = null;

  constructor(
    private blueprintStore: BlueprintStore,
    private workspaceStore: WorkspaceStore,
    private projectStore: ProjectStore,
    private toolbarEventManagerHelper: ToolbarEventManagerHelper,
    private idHelper: IdHelper,
  ) {
    super();
    this.conversationProjectId = this.projectStore.selectedProject?.id ?? '';
    this.watchForDetach();
  }

  get itemContext(): IIntelliCatchItemContext | null {
    return this._itemContext.getValue();
  }

  /**
   * "Generate Tests" on `node`: attaches it to the chat, opens the window and sends the context.
   * Reopening the item that is still attached keeps its session id, so the widget resumes without
   * asking again. Any other click gets a new session id: the widget keeps the conversation, attaches
   * the item and asks its opening request. Earlier sessions stay registered, so their answers still
   * create under their own item.
   */
  public startSession(node: INodeInstanceModel | null | undefined): IIntelliCatchItemContext | null {
    if (!node?.id) {
      return null;
    }

    const held = this.itemContext;
    const sessionId = held?.sessionId && held.nodeId === node.id ? held.sessionId : this.idHelper.generateNewGUID24();
    const context = this.buildContext(node, sessionId);
    this.sessions.set(context.sessionId, {
      context,
      workspaceId: this.workspaceStore.selectedWorkspaceInstance?.id ?? '',
      workspaceKey: this.workspaceStore.selectedWorkspaceInstance?.key ?? '',
      workspaceName: this.workspaceStore.selectedWorkspaceInstance?.name ?? '',
      projectId: this.projectStore.selectedProject?.id ?? '',
      projectKey: this.projectStore.selectedProject?.key ?? '',
      projectName: this.projectStore.selectedProject?.name ?? '',
    });
    this._itemContext.next(context);

    this.isOpeningForItem = true;
    this.showWindow();
    this.isOpeningForItem = false;

    this.send(this.buildSetContextMessage(context));
    return context;
  }

  /**
   * Stops linking new answers to the attached item and tells the widget why, in words the user can
   * be shown as they are. The item's session is kept: its answers still create under it.
   */
  public detach(reason: string | null | undefined): void {
    const held = this.itemContext;
    if (!held) {
      return;
    }

    this._itemContext.next(null);
    const payload: IIntelliCatchClearItemContextPayload = { sessionId: held.sessionId, reason: (reason ?? '').trim() };
    this.send({ action: IntelliCatchMessageAction.ClearItemContext, payload });
  }

  /** The item session an answer was generated for, or null for a general answer or one this conversation no longer knows. */
  public getSession(sessionId: string | null | undefined): IIntelliCatchItemSession | null {
    const id = (sessionId ?? '').trim();
    return id ? this.sessions.get(id) ?? null : null;
  }

  /**
   * Where a create from the answer generated for `sessionId` goes. A blank session is the general
   * chat, which creates loose Tests. Otherwise the session has to be one attached during this
   * conversation, and the user has to be in the project its item lives in and in a label that holds
   * the item: an item can live in several labels, and the Test is created in the one the user is in.
   */
  public resolveCreateTarget(sessionId: string | null | undefined): IIntelliCatchCreateTarget {
    const id = (sessionId ?? '').trim();
    if (!id) {
      return { itemContext: null, refusal: '' };
    }

    const session = this.sessions.get(id);
    if (!session?.context?.nodeId) {
      return { itemContext: null, refusal: UiMessageConstants.INTELLICATCH_CREATE_SESSION_ENDED };
    }

    const workspaceId = this.workspaceStore.selectedWorkspaceInstance?.id ?? '';
    const projectId = this.projectStore.selectedProject?.id ?? '';
    const isInItemLabel = workspaceId === session.workspaceId || this.isItemInSelectedLabel(session.context.nodeId);
    if (!isInItemLabel || projectId !== session.projectId) {
      return {
        itemContext: null,
        refusal: UiMessageConstants.intelliCatchCreateElsewhereMessage(session.context.key, session.context.summary),
      };
    }

    return { itemContext: session.context, refusal: '' };
  }

  /**
   * The user detached or re-attached from inside the chat, or started a new one. Blank is a detach;
   * the widget has already said so on screen, so nothing is sent back.
   */
  public applyWidgetAttachment(sessionId: string | null | undefined): void {
    const id = (sessionId ?? '').trim();
    const context = id ? this.sessions.get(id)?.context ?? null : null;
    if (id && !context) {
      return;
    }
    this._itemContext.next(context);
  }

  /**
   * Forgets every item session and has the widget start the conversation over, saying why. Another
   * project can have another template, and its answers could only be refused there.
   */
  public resetConversation(reason: string | null | undefined): void {
    this.sessions.clear();
    this.generatingSessionId = null;
    this._itemContext.next(null);
    this._resultsReady.next(false);
    const payload: IIntelliCatchResetConversationPayload = { reason: (reason ?? '').trim() };
    this.send({ action: IntelliCatchMessageAction.ResetConversation, payload });
  }

  /** An answer started or finished in the widget. One that finishes unseen is flagged on the header button. */
  public applyGenerationState(sessionId: string | null | undefined, isGenerating: boolean): void {
    if (isGenerating) {
      this.generatingSessionId = (sessionId ?? '').trim();
      return;
    }
    if (this.generatingSessionId === null) {
      return;
    }
    this.generatingSessionId = null;
    if (!this.toolbarEventManagerHelper.CommonToolbarIntelliCatchWindowOpen) {
      this._resultsReady.next(true);
    }
  }

  private showWindow(): void {
    this.toolbarEventManagerHelper.CommonToolbarIntelliCatchWindowOpen = true;
  }

  private watchForDetach(): void {
    // Closing the window only hides it, so it changes nothing here. Opening it from the page header
    // is the general chat: the conversation stays, but new answers stop linking to the item.
    this.subscribers.intelliCatchWindowOpen = this.toolbarEventManagerHelper.$CommonToolbarIntelliCatchWindowOpen.subscribe((isOpen) => {
      if (!isOpen) {
        return;
      }
      this._resultsReady.next(false);
      const held = this.itemContext;
      if (held && !this.isOpeningForItem) {
        this.detach(UiMessageConstants.intelliCatchItemDetachedMessage(UiMessageConstants.INTELLICATCH_DETACH_CAUSE_GENERAL_CHAT, held.key, held.summary));
      }
    });

    // Answers about an item in another label would be created where the user no longer is, so moving
    // away detaches new answers from it; its earlier answers create again once the user is back. An
    // emission carrying no id at all is a reset rather than a switch, and is not treated as one.
    this.subscribers.selectedWorkspace = this.workspaceStore.$selectedWorkspaceInstance.subscribe((workspace) => {
      const held = this.heldSession();
      const workspaceId = workspace?.id ?? '';
      if (!held || !workspaceId || !held.workspaceId || workspaceId === held.workspaceId) {
        return;
      }
      this.detach(UiMessageConstants.intelliCatchItemLeftLabelMessage(held.context.key, held.context.summary));
    });

    this.subscribers.selectedProject = this.projectStore.$selectedProject.subscribe((project) => {
      const projectId = project?.id ?? '';
      if (!projectId || projectId === this.conversationProjectId) {
        return;
      }
      const isSwitch = !!this.conversationProjectId;
      this.conversationProjectId = projectId;
      if (isSwitch) {
        this.resetConversation(UiMessageConstants.INTELLICATCH_CONVERSATION_RESET_PROJECT_CHANGED);
      }
    });
  }

  // The selected workspace is the filtered clone, so an item the active filter hides is only in the full one.
  private isItemInSelectedLabel(nodeId: string): boolean {
    const selected = this.workspaceStore.selectedWorkspaceInstance;
    const full = this.workspaceStore.rawWorkspaceInstance;
    const workspace = full && selected?.id && full.id === selected.id ? full : selected;
    return !!workspace?.nodeInstances?.some((node) => node?.id === nodeId);
  }

  private heldSession(): IIntelliCatchItemSession | null {
    const held = this.itemContext;
    return held ? this.sessions.get(held.sessionId) ?? null : null;
  }

  private buildContext(node: INodeInstanceModel, sessionId: string): IIntelliCatchItemContext {
    const description = richTextToPlainText(node?.description, INTELLICATCH_ITEM_CONTEXT_DESCRIPTION_MAX_CHARS);
    return {
      sessionId,
      nodeId: node?.id ?? '',
      key: (node?.key ?? '').trim(),
      itemType: this.resolveItemType(node?.nodeTypeId),
      summary: (node?.name ?? '').trim(),
      description: description.text,
      descriptionTruncated: description.truncated,
    };
  }

  private resolveItemType(nodeTypeId: string | null | undefined): string {
    if (!nodeTypeId) {
      return '';
    }
    const nodeType = this.blueprintStore.selectedBluePrint?.nodes?.find((node) => node?.id === nodeTypeId);
    return nodeType?.label || nodeType?.name || '';
  }

  private buildSetContextMessage(context: IIntelliCatchItemContext): IIntelliCatchHostMessage {
    const payload: IIntelliCatchSetItemContextPayload = {
      sessionId: context.sessionId,
      key: context.key,
      itemType: context.itemType,
      summary: context.summary,
      description: context.description,
      descriptionTruncated: context.descriptionTruncated,
      initialPrompt: UiMessageConstants.intelliCatchGenerateTestsPrompt(context.summary),
    };
    return { action: IntelliCatchMessageAction.SetItemContext, payload };
  }

  private send(message: IIntelliCatchHostMessage | null): void {
    if (!message) {
      return;
    }
    this.clearDeliveryTimer();
    this.pendingMessage = message;
    this.attemptDelivery(DELIVERY_ATTEMPTS);
  }

  private attemptDelivery(attemptsLeft: number): void {
    const message = this.pendingMessage;
    if (!message) {
      return;
    }

    const iframe = this.findChatIframe();
    const targetOrigin = this.resolveWidgetOrigin(iframe);
    const target = iframe?.contentWindow ?? null;

    if (!iframe || !target || !targetOrigin) {
      if (attemptsLeft <= 1) {
        this.pendingMessage = null;
        return;
      }
      this.deliveryTimer = setTimeout(() => this.attemptDelivery(attemptsLeft - 1), DELIVERY_RETRY_MS);
      return;
    }

    this.pendingMessage = null;
    target.postMessage(message, targetOrigin);
    this.watchIframeLoad(iframe);
  }

  private watchIframeLoad(iframe: HTMLIFrameElement): void {
    if (!iframe || this.watchedIframe === iframe) {
      return;
    }
    this.watchedIframe = iframe;
    iframe.addEventListener('load', this.resendHeldContext);
  }

  private resendHeldContext = (): void => {
    const held = this.itemContext;
    if (!held) {
      return;
    }
    this.send(this.buildSetContextMessage(held));
  };

  private findChatIframe(): HTMLIFrameElement | null {
    return document.querySelector(CHAT_IFRAME_SELECTOR) as HTMLIFrameElement | null;
  }

  private resolveWidgetOrigin(iframe: HTMLIFrameElement | null): string {
    const src = iframe?.getAttribute('src') ?? '';
    if (!src) {
      return '';
    }
    try {
      const origin = new URL(src, window.location.href).origin;
      return origin && origin.toLowerCase() !== 'null' ? origin : '';
    } catch {
      return '';
    }
  }

  private clearDeliveryTimer(): void {
    if (this.deliveryTimer) {
      clearTimeout(this.deliveryTimer);
      this.deliveryTimer = null;
    }
  }

  public ngOnDestroy(): void {
    this.clearDeliveryTimer();
    this.watchedIframe?.removeEventListener('load', this.resendHeldContext);
    this.watchedIframe = null;
    super.ngOnDestroy();
  }
}
